import {Button, InputNumber, Switch, Typography} from "antd";
import type {Raid} from "@/raids/rest/RaidSchema.ts";
import {InputNumberLocalized} from "@/components/InputNumberLocalized.tsx";
import {useEffect, useState} from "react";

const SAND_PER_RUN = 10_000;
const SAND_PER_RUN_LANDSRAAT = 7_500;

type Props = {
  raid: Raid;
  drawerOpen: boolean;
  loading: boolean;
  onCalculate: (
    totalSpiceSand: number,
    landsraatBonus: boolean,
    bonusConversionCount: number,
    withoutTime: boolean,
  ) => void;
};

export function MelangePreviewInput({raid, drawerOpen, loading, onCalculate}: Props) {
  const [totalSpiceSand, setTotalSpiceSand] = useState<number>(raid.spiceReadOnly);
  const [landsraatBonus, setLandsraatBonus] = useState(false);
  const [bonusConversionCount, setBonusConversionCount] = useState(0);
  const [withoutTime, setWithoutTime] = useState(false);

  const sandPerRun = landsraatBonus ? SAND_PER_RUN_LANDSRAAT : SAND_PER_RUN;
  const maxConversionRuns = sandPerRun > 0 ? Math.floor(Math.max(0, totalSpiceSand) / sandPerRun) : 0;

  useEffect(() => {
    if (!drawerOpen) return;
    setTotalSpiceSand(raid.spiceReadOnly);
    setLandsraatBonus(false);
    setBonusConversionCount(0);
    setWithoutTime(false);
  }, [drawerOpen, raid.id, raid.spiceReadOnly]);

  useEffect(() => {
    setBonusConversionCount((b) => Math.min(b, maxConversionRuns));
  }, [maxConversionRuns]);

  const participantsMissingId = raid.participants.some((p) => p.id == null);

  function handleSubmit() {
    onCalculate(totalSpiceSand, landsraatBonus, bonusConversionCount, withoutTime);
  }

  const canSubmit =
    !loading &&
    !participantsMissingId &&
    raid.participants.length > 0 &&
    totalSpiceSand >= 0;

  const hint = participantsMissingId
    ? "Alle Teilnehmer brauchen eine gespeicherte ID — Raid zuerst speichern."
    : raid.participants.length === 0
      ? "Mindestens ein Teilnehmer nötig."
      : null;

  return (
    <div className="flex flex-col gap-3">
      <Typography.Text strong>Voraussetzungen</Typography.Text>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <span>Spice-Sand gesamt</span>
          <InputNumberLocalized
            min={0}
            className="w-40!"
            value={totalSpiceSand}
            onChange={(v) => setTotalSpiceSand(typeof v === "number" ? v : 0)}
            disabled={loading}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span>Landsraat-Bonus (−25&nbsp;% Sand / Lauf)</span>
          <Switch checked={landsraatBonus} onChange={setLandsraatBonus} disabled={loading} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span>Ohne Zeitgewichtung</span>
          <Switch checked={withoutTime} onChange={setWithoutTime} disabled={loading} />
        </div>
        <div className="flex items-start justify-between gap-3">
          <span>Bonus-Procs (45&nbsp;% mehr Melange/Rückstände)</span>
          <div className="flex items-end gap-2">
          <InputNumber
            min={0}
            max={maxConversionRuns}
            className="w-28!"
            value={bonusConversionCount}
            onChange={(v) => setBonusConversionCount(typeof v === "number" ? v : 0)}
            disabled={loading || maxConversionRuns === 0}
          />
          </div>
        </div>
        <Typography.Text type="secondary" className="text-xs! text-right">
          max. {maxConversionRuns} (bei {sandPerRun.toLocaleString("de-DE")} Sand/Lauf)
        </Typography.Text>
      </div>
      {hint && <Typography.Text type="warning">{hint}</Typography.Text>}
      <Button type="primary" onClick={handleSubmit} loading={loading} disabled={!canSubmit}>
        Verteilung berechnen
      </Button>
    </div>
  );
}
